import moment from 'moment-timezone';
import { SnapshotDateRange } from 'src/components/star-grid/reportSelectors.types';

const WEEK_DAYS = 7;
export const VERSION_CALENDAR_MONTH_COUNT = 2;
const DATE_FORMAT = 'YYYY-MM-DD';
const dateValue = (value: string) => moment.utc(value, DATE_FORMAT, true);

export const initialVersionCalendarMonth = (range: SnapshotDateRange): string => {
  const end = dateValue(range.endDate);
  const anchor = end.isValid() ? end : moment.utc();
  return anchor.startOf('month').subtract(1, 'month').format(DATE_FORMAT);
};

export const moveVersionCalendarMonth = (month: string, offset: number): string =>
  dateValue(month).add(offset, 'month').startOf('month').format(DATE_FORMAT);

export const versionCalendarMonthForDate = (month: string, date: string): string => {
  if (!dateValue(date).isValid()) return month;
  if (date < month) return dateValue(date).startOf('month').format(DATE_FORMAT);
  if (date >= moveVersionCalendarMonth(month, VERSION_CALENDAR_MONTH_COUNT)) return moveVersionCalendarMonth(date, -1);
  return month;
};

export const versionCalendarKeyboardDate = (date: string, key: string, shift: boolean): string | undefined => {
  const day = dateValue(date);
  const offsets: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -WEEK_DAYS, ArrowDown: WEEK_DAYS };
  if (key in offsets) return day.add(offsets[key], 'day').format(DATE_FORMAT);
  if (key === 'Home') return day.startOf('isoWeek').format(DATE_FORMAT);
  if (key === 'End') return day.endOf('isoWeek').format(DATE_FORMAT);
  if (key === 'PageUp' || key === 'PageDown') return day.add(key === 'PageUp' ? -1 : 1, shift ? 'year' : 'month').format(DATE_FORMAT);
  return undefined;
};

const calendarDay = (month: string, number: number, range: SnapshotDateRange) => {
  const value = dateValue(month).date(number);
  const date = value.format(DATE_FORMAT);
  return {
    date,
    number,
    label: value.format('dddd, MMMM D, YYYY'),
    today: value.isSame(moment.utc(), 'day'),
    endpoint: date === range.startDate || date === range.endDate,
    withinRange: Boolean(range.startDate && range.endDate && date > range.startDate && date < range.endDate)
  };
};

export const versionCalendarMonth = (month: string, range: SnapshotDateRange, focused: string) => {
  const first = dateValue(month);
  const offset = first.isoWeekday() - 1;
  const count = Math.ceil((offset + first.daysInMonth()) / WEEK_DAYS);
  const weeks = Array.from({ length: count }, (_, week) =>
    Array.from({ length: WEEK_DAYS }, (_, column) => {
      const number = week * WEEK_DAYS + column - offset + 1;
      return number > 0 && number <= first.daysInMonth() ? calendarDay(month, number, range) : null;
    })
  );
  const tabStop = focused.slice(0, 7) === month.slice(0, 7) ? focused : month;
  return { month, label: first.format('MMMM YYYY'), weeks, tabStop };
};

export const versionCalendarWeekdays = () =>
  Array.from({ length: WEEK_DAYS }, (_, index) => {
    const date = moment.utc().isoWeekday(index + 1);
    return { label: date.format('dddd'), short: date.format('dd').slice(0, 1) };
  });
